import type { Comment, PublicUser, ReactionSummary } from "../types";

async function request<T>(url: string, init?: RequestInit) {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {})
    }
  });

  if (!response.ok) {
    const details = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(details?.error ?? "Request failed.");
  }

  return response.json() as Promise<T>;
}

export function getSession() {
  return request<PublicUser | null>("/api/session");
}

export function login(email: string, password: string) {
  return request<PublicUser>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
}

export function oauthUrl(provider: "github" | "linkedin", next = window.location.pathname) {
  return `/api/auth/oauth/${provider}?next=${encodeURIComponent(next)}`;
}

export async function startOAuth(provider: "github" | "linkedin", signal?: AbortSignal) {
  const response = await fetch(`/api/auth/oauth/${provider}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    signal
  });

  if (!response.ok) {
    throw new Error(response.status === 503
      ? `Sign-in with ${provider === "github" ? "GitHub" : "LinkedIn"} is temporarily unavailable. ${provider === "linkedin" ? "You can continue with GitHub." : "Try again later."}`
      : response.status === 429
        ? "Too many attempts. Wait a moment and try again."
        : "Unable to start authentication. Try again.");
  }

  const result = await response.json().catch(() => null) as { url?: string } | null;
  if (typeof result?.url !== "string") throw new Error("Unable to start authentication. Try again.");
  try {
    const url = new URL(result.url);
    if (url.protocol !== "https:") throw new Error();
    return { url: url.href };
  } catch {
    throw new Error("Unable to start authentication. Try again.");
  }
}

export function logout() {
  return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export function listComments(postId: string) {
  return request<Comment[]>(`/api/comments?post_id=${encodeURIComponent(postId)}`);
}

export function createComment(postId: string, body: string) {
  return request<Comment>("/api/comments", {
    method: "POST",
    body: JSON.stringify({ post_id: postId, body })
  });
}

export function getReactions(postId: string) {
  return request<ReactionSummary>(`/api/reactions?post_id=${encodeURIComponent(postId)}`);
}

export function setReaction(postId: string, reaction: "like" | "dislike") {
  return request<{ ok: true }>("/api/reactions", {
    method: "POST",
    body: JSON.stringify({ post_id: postId, reaction })
  });
}
